import { ActionIcon, Flex, Stack, Text } from '@mantine/core';
import { FC } from 'react';
import { LivePostProps } from './types';
import { convertDateToLocal, formatDisplayHours } from '@utils/dates';
import { ShareIcon } from '@heroicons/react/24/outline';
import { useStyles } from './styles';
import { MenuLivePost } from '@organisms/backoffice/MenuLivePost';
import { useTranslation } from 'next-i18next';
import { Badge } from '@atoms/Badge';
import AuthorsCard from '@molecules/frontoffice/AuthorsCard';
import { EditorJsRenderLiveBlog } from '@organisms/EditorJsRender';

export const LivePost: FC<LivePostProps> = ({
  livePost,
  onClickRejectApprove,
  handleUpdateFormValues,
  handleSharePost,
  role,
}) => {
  const { t } = useTranslation('liveBlogDetails');
  const { classes } = useStyles();

  const onClickDelete = () => {
    if (handleUpdateFormValues) {
      handleUpdateFormValues(livePost.id, true);
    }
  };

  const onClickEdit = () => {
    if (handleUpdateFormValues) {
      handleUpdateFormValues(livePost.id);
    }
  };

  const handleClickShare = () => {
    handleSharePost(livePost.slug);
  };

  return (
    <Flex direction="column" className={classes.flex} pb={8} id={livePost.slug}>
      <Flex justify="space-between" mb={15} align="center" w="100%" pt={30}>
        <Flex direction="column">
          <Flex direction="column">
            {handleUpdateFormValues && (
              <Badge
                radius={0}
                color={
                  livePost.rejected_at
                    ? 'BADGE_DARK_RED'
                    : livePost.approved_at
                    ? 'BADGE_GREEN'
                    : 'BADGE_YELLOW'
                }
              >
                {livePost.rejected_at
                  ? t('livePost.table.rejected')
                  : livePost.approved_at
                  ? t('livePost.table.approved')
                  : t('livePost.table.notApproved')}
              </Badge>
            )}
            <Text
              mt={handleUpdateFormValues && 8}
              variant="caption1Orange"
              component="span"
            >
              {convertDateToLocal(livePost.created_at)}
            </Text>
            <Text variant="caption1Gray" mt={2} component="span">
              {formatDisplayHours(livePost.created_at)}
            </Text>
          </Flex>
        </Flex>
        <Flex align="center" gap={10} maw="70%">
          <AuthorsCard
            size={48}
            author={livePost.author}
            padding={0}
            isNameFirst="rtl"
          />

          {handleUpdateFormValues && (
            <MenuLivePost
              onClickRejectApprove={
                livePost.approved_at || livePost.rejected_at
                  ? undefined
                  : onClickRejectApprove
              }
              onClickEdit={onClickEdit}
              onClickDelete={onClickDelete}
              slug={livePost.slug}
              role={role}
            />
          )}
        </Flex>
      </Flex>
      <Stack spacing="xs">
        <Text variant="subtitle1" component="p">
          {livePost.title}
        </Text>
        <EditorJsRenderLiveBlog data={livePost.description} />
      </Stack>
      <Flex justify="end" mt={6}>
        <ActionIcon
          aria-label={`$link-${livePost.slug}`}
          id={`$link-${livePost.slug}`}
          onClick={handleClickShare}
        >
          <ShareIcon width={20} />
        </ActionIcon>
      </Flex>
    </Flex>
  );
};
